<template>
  <GfrLayoutAside class="app-docs-aside" :data-state="showMenu ? 'open' : 'closed'">
    <div class="app-docs-aside-inner">
      <div class="mb-4 flex-shrink-0 max-lg:mb-2">
        <div class="flex items-center">
          <GfrNewButton class="app-toggle-btn lg:!hidden" @click="toggleDocumentMenu">
            <IconsReturn class="h-4 w-4 rotate-180" />
          </GfrNewButton>
          <DocumentInputFilter
            v-model="filterValue"
            v-model:trigger="filterTrigger"
            class="flex-1"
            :placeholder="formatTransify('UGC_OFFICIAL_WEBSITE_FILTER_TEXT')"
          />
        </div>
        <CommonCheckBox
          v-if="openObsoletedBtn"
          id="showObsoleted"
          class="mt-3 text-light max-lg:mt-1 max-lg:pl-10"
          v-model:checked="showObsoleted"
        >
          {{ formatTransify('UGC_OFFICIAL_WEBSITE_OBSOLETE_TIPS1') }}
        </CommonCheckBox>
      </div>
      <div v-if="tabList.length > 1" class="app-docs-tabs">
        <div
          v-for="item in tabList"
          :key="item.value"
          class="app-docs-tab"
          :data-state="item.value === activeTab ? 'active' : 'inactive'"
          @click="toggleTab(item.value)"
        >
          <span>{{ item.label }}</span>
        </div>
      </div>
      <div v-show="filterTrigger" class="mb-2.5 text-m-p text-light max-lg:mb-5" v-html="formatFilterTip" />
      <GfrScrollArea
        v-model:reset="resetScroll"
        class="app-docs-catalog-wrapper"
        :initial-y="scrollAreaY"
        :dir="$route.params.lang === 'ar' ? 'rtl' : 'ltr'"
      >
        <GfrCatalog
          :data="currentCatalog"
          :active-url="String($route.params.id)"
          :prefix-path="prefixPath"
          :show-obsoleted="hasObsoleted"
        />
      </GfrScrollArea>
    </div>
  </GfrLayoutAside>
</template>
<script lang="ts" setup>
import { PropType, ref } from 'vue'
import DocumentInputFilter from './InputFilter.vue'
import type { CatalogsConfig, TabConfig } from '~/components/gfr/types/components'
const props = defineProps({
  catalog: {
    type: Array as PropType<CatalogsConfig[]>,
    required: true
  },
  prefixPath: {
    type: String,
    required: true
  },
  openObsoletedBtn: {
    type: Boolean,
    default: false
  },
  tabList: {
    type: Array as PropType<TabConfig[]>,
    default: () => [
      {
        value: 'all',
        label: 'all'
      }
    ]
  },
  activeTab: {
    type: String,
    default: 'all'
  }
})
const emit = defineEmits(['update:active-tab', 'close-menu'])
const route = useRoute()
const store = useStore()
const { formatTransify } = store
const documentStore = useDocumentStore()
const { setDocuments, toggleDocumentMenu } = documentStore
const { showMenu } = storeToRefs(documentStore)
const resetScroll = ref(false)
const filterValue = ref('')
const filterTrigger = ref(false)
const showObsoleted = ref(false)
const scrollAreaY = ref(0)
const currentCatalog = computed(() => {
  let data = props.catalog
  if (filterTrigger.value && filterValue.value.trim() !== '') {
    data = filterData(props.catalog, filterValue.value, 'title')
  }
  return data
})
const formatFilterTip = computed(() => {
  return formatTransify('UGC_OFFICIAL_WEBSITE_SEARCHED').replace(
    '{num}',
    `<span class="text-white font-bold">${currentCatalog.value.length.toString()}</span>`
  )
})
const hasObsoleted = computed(() => {
  return !props.openObsoletedBtn || showObsoleted.value
})
const allDocuments = computed(() => {
  return flatDocuments(currentCatalog.value, 'url', hasObsoleted.value)
})
/* --- flat all documents --- */
function flatDocuments(arr: CatalogsConfig[], key: keyof CatalogsConfig, hasObsoleted = true) {
  const newArr: CatalogsConfig[] = []
  arr.forEach((item) => {
    if (item[key] && (hasObsoleted || !item.obsoleted)) {
      newArr.push(item)
    }
    if (item.children) {
      newArr.push(...flatDocuments(item.children, key, hasObsoleted))
    }
  })
  return newArr
}
/* -- Return flat catalogs by filtering -- */
function filterData(arr: CatalogsConfig[], filterVal: string, key: keyof CatalogsConfig = 'title'): CatalogsConfig[] {
  const data = ref<CatalogsConfig[]>([])
  arr.forEach((item) => {
    if (String(item[key]).toLowerCase().includes(filterVal.toLowerCase()) && item.url) {
      const newItem = { ...item, children: [] }
      if (!showObsoleted.value) {
        !newItem.obsoleted && data.value.push(newItem)
      } else {
        data.value.push(newItem)
      }
    }
    if (item.children) {
      const children = filterData(item.children, filterVal, key)
      data.value.push(...children)
    }
  })
  return data.value
}

function toggleTab(value: string) {
  emit('update:active-tab', value)
}
function setScrollAreaY() {
  const activeElem = document.querySelector('.gfr-catalog-header.is__active') as HTMLElement
  scrollAreaY.value = activeElem ? activeElem.offsetTop : 0
}
watch(
  () => route.params.id,
  async () => {
    showMenu.value && toggleDocumentMenu()
    if (route.params.id) {
      setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
      await nextTick()
      setScrollAreaY()
    }
  }
)
watch(
  () => showObsoleted.value,
  () => {
    storage.setItem('showObsoleted', showObsoleted.value)
    setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
  }
)
watch(
  () => [filterTrigger.value, props.activeTab],
  async () => {
    resetScroll.value = true
    await nextTick()
    setScrollAreaY()
  }
)
onMounted(async () => {
  showObsoleted.value = storage.getItem('showObsoleted') || false
  setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
  await nextTick()
  setScrollAreaY()
})
onBeforeUnmount(() => {
  showMenu.value && toggleDocumentMenu()
})
</script>
<style lang="scss" scoped>
.app-docs-aside {
  @apply max-2xl:px-5 relative lg:min-h-[calc(100vh-4.5rem)] z-20 lg:py-6;
  @apply ltr:2xl:pl-11 ltr:2xl:pr-6 rtl:2xl:pl-6 rtl:2xl:pr-11;
  @apply z-20 max-lg:fixed max-lg:left-0 max-lg:top-10 max-lg:h-[calc(100%-2.5rem)] max-lg:w-full max-lg:px-5 max-lg:py-3;
  @apply after:absolute after:top-0 after:-z-10 after:h-full after:w-full after:bg-dark/40;
  @apply after:rtl:left-0 after:ltr:right-0 after:max-lg:bg-[#202227];
  @apply transition-transform duration-500 ease-out data-[state=open]:pointer-events-auto ltr:max-lg:data-[state=closed]:-translate-x-full ltr:max-lg:data-[state=open]:translate-x-0 rtl:max-lg:data-[state=closed]:translate-x-full rtl:max-lg:data-[state=open]:translate-x-0;
}
.app-docs-aside-inner {
  @apply flex lg:max-h-[calc(100vh-6.5rem)] w-[19.5rem] flex-col pb-7 lg:sticky lg:left-0 lg:top-24;
  @apply max-lg:w-full max-lg:h-full max-lg:max-h-[98%] max-lg:pb-2;
}
.app-docs-catalog-wrapper {
  @apply w-full;
}

.app-docs-tabs {
  @apply mb-2 grid h-8 w-full flex-shrink-0 cursor-pointer grid-cols-2 grid-rows-1;
}
.app-docs-tab {
  @apply flex items-center justify-center bg-dark-background text-m-p font-bold uppercase text-light transition-colors duration-200 data-[state=active]:bg-primary data-[state=active]:text-dark-foreground;
  @apply max-lg:text-xs max-lg:leading-4.5 hover:text-primary data-[state=active]:hover:text-dark-foreground;
}
.app-toggle-btn {
  @apply inline-flex z-10 h-8 w-8 bg-dark-background ltr:mr-2 rtl:ml-2;
}
</style>
